import React from 'react';
import { makeStyles, Theme, createStyles, Paper } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        formRow: {
            display: 'flex',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(2),
        },
        formCell: {
            flex: 1,
        },
        formLabel: {
            marginBottom: theme.spacing(0.5),
        },
        panelPaper: {
            padding: '15px 10px 10px 10px',
        },
        panelPaperFillHeight: {
            flex: 1,
            minHeight: 0,
            boxSizing: 'border-box',
            display: 'flex',
            flexFlow: 'column',
            overflow: 'hidden',
        },
        panelHeader: {
            display: 'flex',
            gap: theme.spacing(1),
            backgroundColor: '#F5F6FF',
            padding: '10px',
        },
        panelBodyFillHeight: {
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            overflow: 'hidden',
        },
        panelRow: {
            display: 'flex',
            gap: theme.spacing(1),
            padding: '10px',
        },
        panelCell: {
            flex: 1,
        },
        timelineRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            padding: '10px 0',
        },
        timelineDetails: {
            minWidth: '130px',
            display: 'flex',
            flexFlow: 'column',
            gap: 4,
        },
        timelineContent: {
            flex: 1,
        },
    }),
);

export type SkeletonLoaderVariant = 'text' | 'form' | 'panel' | 'card' | 'shape' | 'timeline';
export type SkeletonLoaderShape = 'text' | 'rect' | 'circle';

interface SkeletonLoaderProps {
    /* Layout style: text lines, form fields, a table panel inside a Paper, a summary card, or a single custom shape */
    variant?: SkeletonLoaderVariant;
    /* Number of rows (text lines / field rows / data rows under the header / stacked shapes) — defaults to 1 for 'shape', 2 for everything else */
    rows?: number;
    /* Number of columns per row — used by 'form' and 'panel' variants */
    columns?: number;
    /* Per-row width/height overrides for the 'text' variant — falls back to the built-in cosmetic widths when omitted */
    widths?: (string | number)[];
    heights?: (string | number)[];
    /* 'panel' variant only — set false to render the header/rows without the Paper wrapper */
    wrapInPaper?: boolean;
    /* 'panel' variant only — stretch to the full height of the parent, spreading rows evenly across it (e.g. an ag-grid replacement) instead of the default fixed number of rows sized to content */
    fillHeight?: boolean;
    /* 'shape' variant only — a direct stand-in for a single MUI Skeleton (icon, avatar, textarea placeholder, etc.) */
    shape?: SkeletonLoaderShape;
    width?: string | number;
    height?: string | number;
    animation?: 'pulse' | 'wave' | false;
    style?: React.CSSProperties;
    className?: string;
}

/*
 * SkeletonLoader — reusable loading placeholder.
 *
 * Variants:
 *   text  — stacked text-width lines; good for header/detail label sections.
 *   form  — rows of label + input rectangle pairs; good for full dialog loading.
 *   panel — Paper with a shaded column header row and data rows; good for
 *            info panels (PhoneMatterDetailsPanel, PhoneContactDetailsPanel, etc.).
 *   card  — icon rectangle + title line + description lines; good for summary/nav cards.
 *   shape — a single custom skeleton (shape: 'text' | 'rect' | 'circle'); a direct
 *            stand-in for a one-off MUI Skeleton (icons, avatars, buttons, textarea placeholders).
 *   timeline — rows of avatar circle + detail lines + content block; good for
 *            vertical activity/history timelines (HistoryActivity, etc.).
 */
export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
    variant = 'form',
    rows,
    columns = 2,
    widths,
    heights,
    wrapInPaper = true,
    fillHeight = false,
    shape = 'text',
    width = '100%',
    height,
    animation,
    style,
    className,
}) => {
    const classes = useStyles();

    // 'shape' is a single custom skeleton by default — every other variant repeats rows
    const resolvedRows = rows ?? (variant === 'shape' ? 1 : 2);

    if (variant === 'shape') {
        return (
            <div className={className}>
                {Array.from({ length: resolvedRows }).map((_, i) => (
                    <Skeleton
                        key={i}
                        variant={shape}
                        width={width}
                        height={height}
                        animation={animation}
                        style={i > 0 ? { ...style, marginTop: 4 } : style}
                    />
                ))}
            </div>
        );
    }

    if (variant === 'text') {
        const defaultWidths = ['55%', '75%', '65%', '80%', '45%'];
        return (
            <div className={className}>
                {Array.from({ length: resolvedRows }).map((_, i) => (
                    <Skeleton
                        key={i}
                        variant="text"
                        width={widths ? widths[i % widths.length] : defaultWidths[i % defaultWidths.length]}
                        height={heights ? heights[i % heights.length] : undefined}
                        style={i > 0 && heights ? { marginTop: 4 } : undefined}
                    />
                ))}
            </div>
        );
    }

    if (variant === 'panel') {
        const rows = (
            <>
                {Array.from({ length: resolvedRows }).map((_, rowIdx) => (
                    <div key={rowIdx} className={classes.panelRow}>
                        {Array.from({ length: columns }).map((_, colIdx) => (
                            <div key={colIdx} className={classes.panelCell}>
                                <Skeleton variant="text" />
                            </div>
                        ))}
                    </div>
                ))}
            </>
        );

        const panelContent = (
            <>
                <div className={classes.panelHeader}>
                    {Array.from({ length: columns }).map((_, i) => (
                        <div key={i} className={classes.panelCell}>
                            <Skeleton variant="text" />
                        </div>
                    ))}
                </div>
                {fillHeight ? <div className={classes.panelBodyFillHeight}>{rows}</div> : rows}
            </>
        );

        if (!wrapInPaper) {
            return <div className={className}>{panelContent}</div>;
        }

        return (
            <Paper
                elevation={3}
                className={`${classes.panelPaper}${fillHeight ? ` ${classes.panelPaperFillHeight}` : ''}${className ? ` ${className}` : ''}`}
            >
                {panelContent}
            </Paper>
        );
    }

    if (variant === 'timeline') {
        return (
            <div className={className}>
                {Array.from({ length: resolvedRows }).map((_, i) => (
                    <div key={i} className={classes.timelineRow}>
                        <Skeleton variant="circle" width={36} height={36} />
                        <div className={classes.timelineDetails}>
                            <Skeleton variant="text" width="70%" />
                            <Skeleton variant="text" width="40%" height={14} />
                        </div>
                        <div className={classes.timelineContent}>
                            <Skeleton variant="rect" height={48} style={{ borderRadius: 4 }} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (variant === 'card') {
        const descriptionWidths = ['90%', '70%', '80%', '60%'];
        return (
            <div className={className}>
                <Skeleton variant="rect" width={40} height={40} style={{ borderRadius: 10, marginBottom: 12 }} />
                <Skeleton variant="text" width="55%" height={18} style={{ marginBottom: 4 }} />
                {Array.from({ length: resolvedRows }).map((_, rowIndex) => (
                    <Skeleton key={rowIndex} variant="text" width={descriptionWidths[rowIndex % descriptionWidths.length]} height={14} />
                ))}
            </div>
        );
    }

    // form (default)
    return (
        <div className={className}>
            {Array.from({ length: resolvedRows }).map((_, rowIdx) => (
                <div key={rowIdx} className={classes.formRow}>
                    {Array.from({ length: columns }).map((_, colIdx) => (
                        <div key={colIdx} className={classes.formCell}>
                            <Skeleton variant="text" width="40%" className={classes.formLabel} />
                            <Skeleton variant="rect" height={32} style={{ borderRadius: 4 }} />
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};
